import { useJSONFetcher } from "@outplayed/json-fetcher";
import React from "react";
import { getEncounterId, useWowPath } from "../../../hooks/path-hooks";
import {
  getDifficultyPath,
  getFormattedNames,
  PATCH,
  getFormattedEncounterType,
  getIcyVeinsLinkName,
  getIcyVeinsRole,
  EXPANSION,
  isClassHunter,
  getDataHeroName,
  getHeroNameFromSelectedTree,
  getAbbreviatedSpec,
  getAbbreviatedClass,
} from "../../../lib/wowHelpers";
import TalentsContainer from "./TalentsContainer";
import { useQueryString } from "../../../hooks/general-hooks";
import { API_WOW_ROOT_PATH } from "../../../api/apiHelpers";
import SubtitleAndDescription from "../../Common/SubtitleAndDescription";
import NoData from "../../Common/NoData";
import Layout from "../Layout";
import PageSectionCrossLinks from "../../CrossLinks/PageSectionCrossLinks";
import HeroTalentSelector from "../Builds/HeroTalentSelector";
import { useHeroQueryParams } from "hooks/wow/heroQueryParams";
import { SPECS_TO_HERO_TREES } from "lib/classes-and-specs";
import { TablePageSkeleton } from "components/Common/PageSkeletons";

const TalentsPageContainer = () => {
  const { classType, specType, encounterType, optionalParams } = useWowPath();
  const encounterID = getEncounterId(optionalParams, encounterType);
  const { displayClassName, displaySpecName, dataClassName, dataSpecName } = getFormattedNames(classType, specType);
  const { selectedHero, setSelectedHero } = useHeroQueryParams(displayClassName, displaySpecName);
  const dataHeroName = getDataHeroName(getHeroNameFromSelectedTree(selectedHero, displayClassName, displaySpecName));
  const dataOtherHeroName = getDataHeroName(
    getHeroNameFromSelectedTree(selectedHero === "left" ? "right" : "left", displayClassName, displaySpecName),
  );

  const { difficulty, affix } = useQueryString();
  const diffPath = getDifficultyPath(difficulty, encounterType);
  const { data: talentData, loading: isLoading } = useJSONFetcher(
    `${API_WOW_ROOT_PATH}/${diffPath}/${dataClassName}/${dataClassName}_talents.json`,
    { ssr: true },
  );

  const pageDescription = `${displaySpecName} ${displayClassName} with U.GG best data for every build. The best meta ${
    SPECS_TO_HERO_TREES[displayClassName].specs[displaySpecName][0]
  } and ${SPECS_TO_HERO_TREES[displayClassName].specs[displaySpecName][1]} builds for ${getAbbreviatedSpec(
    displaySpecName,
  )} ${getAbbreviatedClass(displayClassName)} by talent popularity in ${EXPANSION} ${PATCH} for ${getFormattedEncounterType(
    encounterType,
  )}.`;

  const metaDescription = `${displaySpecName} ${displayClassName} with U.GG best data for every build. The best meta ${getAbbreviatedSpec(
    displaySpecName,
  )} ${getAbbreviatedClass(
    displayClassName,
  )} hero builds by talent popularity in ${EXPANSION} ${PATCH} for ${getFormattedEncounterType(encounterType)}.`;

  const layoutProps = {
    className: "wow-talents-page-container",
    classType,
    filterPage: "builds",
    metaTitle: `${displaySpecName} ${displayClassName} Optional, Alternate, and Other Builds for ${getFormattedEncounterType(
      encounterType,
    )}`,
    metaDescription,
    pageDescription,
    pageTitle: `Optional, Alternate, and Other Builds for ${getFormattedEncounterType(encounterType)}`,
    specType,
  };

  if (isLoading) {
    return (
      <Layout {...layoutProps}>
        <TablePageSkeleton />
      </Layout>
    );
  }

  const classSpecHero = `${dataClassName}-${dataSpecName}-${dataHeroName}`;
  const otherClassSpecHero = `${dataClassName}-${dataSpecName}-${dataOtherHeroName}`;
  const hasData =
    talentData &&
    (affix && affix !== "all"
      ? (talentData.affixes?.[affix]?.[encounterID]?.[classSpecHero]?.all_talents ?? []).length > 0 ||
        (talentData.affixes?.[affix]?.[encounterID]?.[otherClassSpecHero]?.all_talents ?? []).length > 0
      : (talentData[encounterType]?.[encounterID]?.[classSpecHero]?.all_talents ?? []).length > 0 ||
        (talentData[encounterType]?.[encounterID]?.[otherClassSpecHero]?.all_talents ?? []).length > 0);

  const talents = hasData
    ? affix && affix !== "all"
      ? talentData.affixes[affix][encounterID][classSpecHero].all_talents
      : talentData[encounterType][encounterID][classSpecHero].all_talents
    : [];
  const totalCount = talents?.reduce((partialSum, talent) => partialSum + talent.count, 0);

  return (
    <Layout {...layoutProps}>
      {hasData ? (
        <>
          <HeroTalentSelector
            displayClassName={displayClassName}
            displaySpecName={displaySpecName}
            activeHero={selectedHero}
            onClick={setSelectedHero}
            specType={specType}
            pageType="Builds"
          />
          <SubtitleAndDescription
            className="talents-subtitle"
            subtitle={`Recommended ${displaySpecName} ${displayClassName} Talent Tree Build and Optional Builds`}
          />
          <div className="all-talents-container">
            {talents.map((talent, index) => (
              <TalentsContainer
                classType={classType}
                encounterType={encounterType}
                index={index}
                key={index}
                specType={specType}
                talentData={talent}
                totalCount={totalCount}
                selectedHero={selectedHero}
              />
            ))}
          </div>
          <PageSectionCrossLinks
            classType={classType}
            description={`Our friends at Icy Veins have you covered, with an in-depth guide to provide your ${displaySpecName} ${displayClassName} with the best builds and talents.`}
            guideData={[
              {
                description: `The best in-depth guides covering builds and talents for ${displaySpecName} ${displayClassName}`,
                link: `https://www.icy-veins.com/wow/${getIcyVeinsLinkName(specType)}-${getIcyVeinsLinkName(
                  classType,
                )}-pve-${getIcyVeinsRole(specType)}-spec-builds${isClassHunter(classType)}-talents`,
                title: "Builds and Talents Guide",
              },
            ]}
            specType={specType}
          />
        </>
      ) : (
        <NoData specType={specType} />
      )}
    </Layout>
  );
};

export default TalentsPageContainer;
